import { useRef, useState } from 'react';
import { Crop, RotateCcw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';

const clamp01 = (v) => Math.min(1, Math.max(0, v));

export const CropDialog = ({ open, item, onClose, onApply }) => {
  const boxRef = useRef(null);
  const [rect, setRect] = useState(null);
  const [drag, setDrag] = useState(null);

  const toLocal = (event) => {
    const bounds = boxRef.current?.getBoundingClientRect();
    if (!bounds) return { x: 0, y: 0 };
    return {
      x: clamp01((event.clientX - bounds.left) / bounds.width),
      y: clamp01((event.clientY - bounds.top) / bounds.height),
    };
  };

  const onPointerDown = (event) => {
    event.preventDefault();
    const start = toLocal(event);
    setDrag(start);
    setRect({ x: start.x, y: start.y, width: 0, height: 0 });
  };

  const onPointerMove = (event) => {
    if (!drag) return;
    const now = toLocal(event);
    setRect({
      x: Math.min(drag.x, now.x),
      y: Math.min(drag.y, now.y),
      width: Math.abs(now.x - drag.x),
      height: Math.abs(now.y - drag.y),
    });
  };

  const onPointerUp = () => setDrag(null);

  const apply = () => {
    if (rect && rect.width > 0.03 && rect.height > 0.03) onApply(rect);
    else onApply(null);
    setRect(null);
    onClose();
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : null)}>
      <DialogContent className="max-w-2xl" data-testid="crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Crop “{item.name}”</DialogTitle>
          <DialogDescription>
            Drag across the area that contains the text. Cropping only affects the temporary
            recognition canvas — your original file is never modified.
          </DialogDescription>
        </DialogHeader>

        <div
          ref={boxRef}
          className="relative w-full select-none overflow-hidden rounded-xl border border-[#e6e9f4] bg-slate-100 touch-none"
          data-testid="crop-surface"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        >
          <img
            alt={`Preview of ${item.name} used for choosing a crop area`}
            className="pointer-events-none block max-h-[52vh] w-full object-contain"
            src={item.previewUrl}
            style={{ transform: `rotate(${item.rotation}deg)` }}
          />
          {rect ? (
            <div
              className="pointer-events-none absolute border-2 border-indigo-500 bg-indigo-500/15"
              style={{
                left: `${rect.x * 100}%`,
                top: `${rect.y * 100}%`,
                width: `${rect.width * 100}%`,
                height: `${rect.height * 100}%`,
              }}
            />
          ) : null}
        </div>

        <p className="text-xs text-slate-500">
          {item.crop
            ? 'A crop is currently applied. Drag a new area, or clear it below.'
            : 'No crop applied yet — the full image will be recognised.'}
        </p>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            data-testid="crop-clear-button"
            onClick={() => {
              setRect(null);
              onApply(null);
              onClose();
            }}
            type="button"
            variant="outline"
          >
            <RotateCcw aria-hidden="true" className="mr-2 h-4 w-4" />
            Clear crop
          </Button>
          <Button className="brand-gradient text-white" data-testid="crop-apply-button" onClick={apply} type="button">
            <Crop aria-hidden="true" className="mr-2 h-4 w-4" />
            Apply crop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CropDialog;
